/**
 * Account Store
 *
 * Zustand-based store for Account feature with command-driven architecture.
 *
 * Usage in components:
 * ```typescript
 * import { useAccountSelector, useAccountDispatch, useAccountPolicies, createAccountPolicies } from '@domains/account';
 * import useBackend from '@contexts/useBackend/useBackend';
 *
 * function AccountBilling() {
 *   const { backend } = useBackend();
 *   const dispatch = useAccountDispatch();
 *
 *   // Initialize policies with backend
 *   useAccountPolicies(createAccountPolicies(backend));
 *
 *   // Select state
 *   const entities = useAccountSelector(s => s.billing.entities);
 *
 *   // Dispatch commands
 *   const handleAdd = () => {
 *     dispatch(
 *       { type: 'account/billing/form/openAdd', payload: {} },
 *       { source: 'user', timestamp: Date.now() }
 *     );
 *   };
 * }
 * ```
 */

import { useCallback } from 'react';
import { createStore } from '@commandDrivenStateEngine/core/createStore';
import type { CommandMetaInput, Dispatch } from '@commandDrivenStateEngine/core/types';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import useAuth from '@contexts/useAuth/useAuth';
import { initialState } from './state';
import { handlers } from './handlers';
import { invariants } from './invariants';
import { AccountCommand } from './commands';
import { createAccountSyncPolicy } from './policies/accountSyncPolicy';
import { createAccountInfoUpdatePolicy } from './policies/accountInfoUpdatePolicy';
import { createBillingEntitiesSyncPolicy } from './policies/billingEntitiesSyncPolicy';
import { createBillingEntitySubmitPolicy } from './policies/billingEntitySubmitPolicy';
import { createArcaPointsOfSalePolicy } from './policies/arcaPointsOfSalePolicy';
import { createInvoicingIntegrationsSyncPolicy } from './policies/invoicingIntegrationsSyncPolicy';
import { createUsersFetchPolicy } from './policies/usersFetchPolicy';
import { createUserSubmitPolicy } from './policies/userSubmitPolicy';
import { createRulesFetchPolicy } from './policies/rulesFetchPolicy';
import { createRulesSubmitPolicy } from './policies/rulesSubmitPolicy';
import { createSkuConfigSyncPolicy } from './policies/skuConfigSyncPolicy';
import { createSkuConfigSubmitPolicy } from './policies/skuConfigSubmitPolicy';
import { createOrderStatusesSyncPolicy } from './policies/orderStatusesSync.policy';
import { createOrderStatusesSubmitPolicy } from './policies/orderStatusesSubmit.policy';
import { createGroupsFetchPolicy } from './policies/groupsFetchPolicy';
import { createGroupSubmitPolicy } from './policies/groupSubmitPolicy';

// Create the store (no policies passed here - they need backend)
const {
  useSelector,
  useErrors,
  usePolicies,
  getState,
  dispatch
} = createStore({
  name: 'account',
  initialState,
  handlers,
  invariants
});

/**
 * Dispatch hook that attaches the authenticated user to user-sourced commands
 * so the `canApply` capability gates (`gateCommandByOperation`, TV1-1878) can
 * authorize them. The engine core stays auth-agnostic; this web-app boundary
 * supplies the actor. An explicit `meta.user` (e.g. from tests) is left
 * untouched. Mirrors `useWarehousesDispatch`.
 */
const useDispatch = (): Dispatch<AccountCommand> => {
  const auth = useAuth();
  const user = auth?.user ?? null;
  return useCallback<Dispatch<AccountCommand>>(
    ( command, metaInput: CommandMetaInput ) =>
      dispatch(
        command,
        metaInput.source === 'user' && metaInput.user === undefined
          ? { ...metaInput, user }
          : metaInput
      ),
    [user]
  );
};

/**
 * Policy factory function.
 * Creates all policies with backend dependency.
 *
 * @param backend Backend instance from useBackend hook
 * @param accountId Account ID from user context
 * @returns Array of policies to pass to useAccountPolicies
 */
export const createAccountPolicies = ( backend: ClientLibraryType, accountId: string ) => [
  createAccountSyncPolicy( backend ),
  createAccountInfoUpdatePolicy( backend, accountId ),
  createBillingEntitiesSyncPolicy( backend ),
  createInvoicingIntegrationsSyncPolicy( backend ),
  createBillingEntitySubmitPolicy( backend ),
  // TV1-2210 (S6) — feeds the point-of-sale picker from ARCA's registry.
  createArcaPointsOfSalePolicy( backend ),
  createUsersFetchPolicy( backend, accountId ),
  createUserSubmitPolicy( backend, accountId ),
  createRulesFetchPolicy( backend, accountId ),
  createRulesSubmitPolicy( backend, accountId ),
  createSkuConfigSyncPolicy( backend, accountId ),
  createSkuConfigSubmitPolicy( backend, accountId ),
  createOrderStatusesSyncPolicy( backend ),
  createOrderStatusesSubmitPolicy( backend, accountId ),
  // Assignment groups (TV1-1878)
  createGroupsFetchPolicy( backend, accountId ),
  createGroupSubmitPolicy( backend, accountId )
];

// Export hooks with domain-specific names
export {
  useSelector as useAccountSelector,
  useErrors as useAccountErrors,
  useDispatch as useAccountDispatch,
  usePolicies as useAccountPolicies,
  getState as getAccountState,
  dispatch as dispatchAccountCommand
};

/**
 * Re-export selectors for convenience
 */
export { selectors } from './state';

/**
 * Re-export VAT condition options for forms
 */
export { vatConditionOptions } from './state';
